/**
 * Aceternity UI — Apple Cards Carousel (adapted for Vite + framer-motion + lucide)
 * https://ui.aceternity.com/components/apple-cards-carousel
 */
import React, {
  useEffect,
  useRef,
  useState,
  createContext,
  useContext,
  useCallback,
} from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useOutsideClick } from '../../hooks/use-outside-click';

export type AppleCarouselCardData = {
  src: string;
  title: string;
  category: string;
  content: React.ReactNode;
  /** Shown on the card face (e.g. Plan your visit orb). */
  faceAction?: React.ReactNode;
};

type CarouselContextValue = {
  onCardClose: (index: number) => void;
  currentIndex: number;
};

export const CarouselContext = createContext<CarouselContextValue>({
  onCardClose: () => {},
  currentIndex: 0,
});

export const Carousel = ({
  items,
  initialScroll = 0,
  nextHintLabel = 'Next photo',
}: {
  items: React.ReactNode[];
  initialScroll?: number;
  /** Accessible label for the next-photo guide control. */
  nextHintLabel?: string;
}) => {
  const carouselRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showNextGuide, setShowNextGuide] = useState(true);

  useEffect(() => {
    if (carouselRef.current) {
      carouselRef.current.scrollLeft = initialScroll;
      checkScrollability();
    }
  }, [initialScroll]);

  const checkScrollability = () => {
    if (!carouselRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
    const atEnd = scrollLeft >= scrollWidth - clientWidth - 1;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(!atEnd);
    if (scrollLeft > 24) setShowNextGuide(false);
  };

  const scrollStep = () => (isMobile() ? 312 : 300);

  const scrollLeft = () => {
    carouselRef.current?.scrollBy({ left: -scrollStep(), behavior: 'smooth' });
  };

  const scrollRight = () => {
    setShowNextGuide(false);
    carouselRef.current?.scrollBy({ left: scrollStep(), behavior: 'smooth' });
  };

  const handleCardClose = (index: number) => {
    if (!carouselRef.current) return;
    const cardWidth = isMobile() ? 300 : 384;
    const gap = isMobile() ? 12 : 8;
    const scrollPosition = (cardWidth + gap) * (index + 1);
    carouselRef.current.scrollTo({
      left: scrollPosition,
      behavior: 'smooth',
    });
    setCurrentIndex(index);
  };

  const isMobile = () =>
    typeof window !== 'undefined' && window.innerWidth < 768;

  return (
    <CarouselContext.Provider value={{ onCardClose: handleCardClose, currentIndex }}>
      <div className="relative w-full">
        <div
          className="flex w-full overflow-x-scroll overscroll-x-contain touch-pan-x scroll-smooth py-6 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
          ref={carouselRef}
          onScroll={checkScrollability}
        >
          <div
            className={cn(
              'pointer-events-none absolute right-0 z-[30] h-full w-[18%] bg-gradient-to-l from-[#f3eee7] via-[#f3eee7]/55 to-transparent'
            )}
          />
          <div
            className={cn(
              'flex flex-row justify-start gap-4 pl-4',
              'mx-auto max-w-7xl'
            )}
          >
            {items.map((item, index) => (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{
                  opacity: 1,
                  y: 0,
                  transition: {
                    duration: 0.5,
                    delay: 0.15 * index,
                    ease: 'easeOut',
                  },
                }}
                key={`card-${index}`}
                className="rounded-3xl last:pr-[22%] md:last:pr-[33%]"
              >
                {item}
              </motion.div>
            ))}
          </div>
        </div>

        {/* Flesh cue on the right edge — toward the next card peek */}
        {canScrollRight && showNextGuide ? (
          <button
            type="button"
            onClick={() => {
              setShowNextGuide(false);
              scrollRight();
            }}
            aria-label={nextHintLabel}
            className="absolute top-1/2 right-2 z-40 flex h-[55%] w-14 -translate-y-1/2 items-center justify-end md:right-6 md:w-16"
          >
            <motion.span
              aria-hidden
              className="absolute inset-0 rounded-r-2xl bg-gradient-to-l from-[#3d2817]/35 via-[#3d2817]/10 to-transparent"
              animate={{ opacity: [0.5, 0.95, 0.5] }}
              transition={{ duration: 1.7, repeat: Infinity, ease: 'easeInOut' }}
            />
            <motion.span
              aria-hidden
              className="relative z-10 mr-1 flex h-10 w-10 items-center justify-center rounded-full bg-white/95 text-[#3d2817] shadow-md"
              animate={{ x: [0, 4, 0] }}
              transition={{ duration: 1.35, repeat: Infinity, ease: 'easeInOut' }}
            >
              <ArrowRight className="h-5 w-5" strokeWidth={2.25} />
            </motion.span>
          </button>
        ) : null}

        <div className="mr-4 flex justify-end gap-2">
          <button
            type="button"
            className="relative z-40 flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 disabled:opacity-50"
            onClick={scrollLeft}
            disabled={!canScrollLeft}
            aria-label="Previous"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <button
            type="button"
            className="relative z-40 flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 disabled:opacity-50"
            onClick={scrollRight}
            disabled={!canScrollRight}
            aria-label="Next"
          >
            <ArrowRight className="h-5 w-5 text-gray-600" />
          </button>
        </div>
      </div>
    </CarouselContext.Provider>
  );
};

export const Card = ({
  card,
  index,
  layout = false,
}: {
  card: AppleCarouselCardData;
  index: number;
  layout?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { onCardClose } = useContext(CarouselContext);

  const handleClose = useCallback(() => {
    setOpen(false);
    onCardClose(index);
  }, [index, onCardClose]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') handleClose();
    }
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, handleClose]);

  useOutsideClick(containerRef, () => handleClose());

  const handleOpen = () => setOpen(true);

  return (
    <>
      <AnimatePresence>
        {open && (
          <div className="fixed inset-0 z-[160] h-screen overflow-auto">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 h-full w-full bg-black/80 backdrop-blur-lg"
            />
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              ref={containerRef}
              layoutId={layout ? `card-${card.title}` : undefined}
              className="relative z-[170] mx-auto my-6 h-fit max-w-5xl rounded-3xl bg-white p-4 font-sans md:my-10 md:p-10"
            >
              <button
                type="button"
                className="sticky top-4 right-0 ml-auto flex h-8 w-8 items-center justify-center rounded-full bg-black"
                onClick={handleClose}
                aria-label="Close"
              >
                <X className="h-5 w-5 text-neutral-100" />
              </button>
              <motion.p
                layoutId={layout ? `category-${card.title}` : undefined}
                className="text-base font-medium text-black"
              >
                {card.category}
              </motion.p>
              <motion.p
                layoutId={layout ? `title-${card.title}` : undefined}
                className="mt-2 max-w-lg text-2xl font-semibold text-neutral-800 md:text-4xl"
              >
                {card.title}
              </motion.p>
              <div className="py-6">{card.content}</div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
      <motion.div
        role="button"
        tabIndex={0}
        layoutId={layout ? `card-${card.title}` : undefined}
        onClick={handleOpen}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleOpen();
          }
        }}
        className="relative z-10 flex h-[26rem] w-[min(86vw,20rem)] cursor-pointer flex-col items-start justify-start overflow-hidden rounded-3xl bg-gray-100 md:h-[40rem] md:w-96"
      >
        <div className="pointer-events-none absolute inset-x-0 top-0 z-30 h-full bg-gradient-to-b from-black/55 via-transparent to-transparent" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-30 h-2/5 bg-gradient-to-t from-black/50 via-black/15 to-transparent" />
        <div className="relative z-40 p-5 text-left md:p-6">
          <motion.p
            layoutId={layout ? `category-${card.title}` : undefined}
            className="text-left font-sans text-sm font-medium text-white md:text-base"
          >
            {card.category}
          </motion.p>
          <motion.p
            layoutId={layout ? `title-${card.title}` : undefined}
            className="mt-2 max-w-xs text-left font-sans text-xl font-semibold [text-wrap:balance] text-white md:text-3xl"
          >
            {card.title}
          </motion.p>
        </div>
        {card.faceAction ? (
          <div className="absolute bottom-4 left-1/2 z-50 -translate-x-1/2 md:right-5 md:bottom-5 md:left-auto md:translate-x-0">
            {card.faceAction}
          </div>
        ) : null}
        <BlurImage
          src={card.src}
          alt={card.title}
          className="absolute inset-0 z-10 object-cover"
        />
      </motion.div>
    </>
  );
};

export const BlurImage = ({
  src,
  className,
  alt,
  ...rest
}: {
  src: string;
  className?: string;
  alt?: string;
} & React.ImgHTMLAttributes<HTMLImageElement>) => {
  const [loaded, setLoaded] = useState(false);
  return (
    <img
      src={src}
      onLoad={() => setLoaded(true)}
      className={cn(
        'h-full w-full transition duration-300',
        loaded ? 'blur-0' : 'blur-sm',
        className
      )}
      alt={alt ?? 'Background'}
      {...rest}
    />
  );
};
